// The event types a webhook can subscribe to, grouped for the picker. Generated from the event
// catalog (packages/schema/src/contracts/events.ts); `webhooks.test.tsx` fails when the catalog
// and this list differ, so a new event type is one regeneration away. The web bundle does not
// import the schema package, hence the copy.
export const EVENT_GROUPS: readonly { key: string; label: string; types: readonly string[] }[] = [
  {
    key: "org",
    label: "Company and org",
    types: [
      "company.created",
      "company.updated",
      "company.model_policy_changed",
      "company.agent_mode_changed",
      "membership.created",
      "invite.created",
      "invite.accepted",
      "invite.revoked",
      "invite.updated",
      // GUIDED-FLOW: guided setup runs and their sealed answers
      "flow.sent",
      "flow.step_saved",
      "flow.completed",
      "flow.cancelled",
      "sensitive_value.sealed",
      "membership.updated",
      "membership.deactivated",
      "role.created",
      "role.updated",
      "role.deleted",
      "seat.created",
      "seat.updated",
      "seat.assigned",
      "seat.unassigned",
      "seat.closed",
      "digital_employee.created",
      "digital_employee.updated",
      "digital_employee.activated",
      "digital_employee.paused",
      "digital_employee.resumed",
      "digital_employee.retired",
      "digital_employee.litellm_key_created",
      "digital_employee.provisioning_failed",
      "digital_employee.litellm_key_delivered",
      "digital_employee.removed",
      // PURGE-RETIRED-AGENTS: a retired DE or AG removed for good (ClawPanel listens)
      "agent.purged",
      "digital_employee.job_changed",
      "external_identity.created",
      "external_identity.closed",
      "team.created",
      "team.updated",
      "team.deleted",
      "team.member_changed",
      "seat_edge.changed",
      "responsibility.created",
      "responsibility.updated",
      "responsibility.removed",
      "goal.created",
      "goal.updated",
      "goal.track_changed",
      "goal.milestone_done",
      "goal.deleted",
      "kpi_grant.changed",
      "recurring_work.created",
      "recurring_work.updated",
      "recurring_work.deleted",
      "recurring_work.materialised",
      "template.created",
      "template.updated",
      "template.deleted",
      "template.applied",
      "webhook.created",
      "webhook.updated",
      "webhook.deleted",
      "webhook.tested",
      "webhook.secret_rotated",
      "webhook.delivery_retried",
      "webhook.disabled",
      // WOS-BUNDLE
      "webhook.paused",
      "webhook.resumed",
      // WEBHOOKS-IN: incoming webhooks
      "inbound_webhook.created",
      "inbound_webhook.updated",
      "inbound_webhook.deleted",
      "inbound_webhook.secret_rotated",
      "inbound_webhook.received",
      "inbound_webhook.paused",
      "inbound_webhook.signatures_failing",
      "inbound_webhook.resumed",
      "inbound_webhook.disabled",
      // INC2-INTAKE: the one intake
      "inbound_event.received",
      "inbound_rule.changed",
      // INC2-CONTACT: the contact core
      "contact.upserted",
      "contact.identity_linked",
      "contact.consent_recorded",
      "company.model_access_changed",
      "company.export_requested",
      "company.export_completed",
      "company.export_failed",
      "client_survey.sent",
      "client_survey.answered",
      "request.accepted",
      "request.rejected",
      "request.delivered",
    ],
  },
  {
    key: "access",
    label: "Access",
    types: [
      "company.entitlement_changed",
      "agency_grant.created",
      "access.granted",
      "access.revoked",
      "agent_key.created",
      "agent_key.revoked",
      "agent_key.rotated",
      "access.break_glass_requested",
      "access.break_glass_started",
      "access.break_glass_rejected",
      "access.break_glass_ended",
      "access.break_glass_read",
      "access.changed",
      "oauth_client.changed",
    ],
  },
  {
    key: "founder_os",
    label: "Founder OS",
    types: [
      "account.created",
      "portfolio.company_added",
      "portfolio.company_removed",
      "linked_seat.created",
      "linked_seat.removed",
      "linked_seat.moved",
      "goal.cascaded",
      "goal.uncascaded",
      "founder_brief.generated",
      "portfolio_projection.published",
    ],
  },
  {
    key: "measurement",
    label: "Measurement",
    types: [
      "seat_metric.changed",
      "team_metric.changed",
      "metric_def.created",
      "metric_def.updated",
      "metric_def.archived",
      "metric_def.unarchived",
      "kpi.entry_due",
      "target_rule.changed",
      "observation.recorded",
      "observation.recomputed",
      "reporting_duty.changed",
      "reporting_duty.due",
      "reporting_duty.late",
    ],
  },
  {
    key: "cadence",
    label: "Issues and meetings",
    types: [
      "issue.raised",
      "issue.updated",
      "issue.status_changed",
      "issue.resolved",
      "issue.dropped",
      "issue.escalated",
      "issue.deescalated",
      "issue.deleted",
      "meeting_series.changed",
      "meeting.scheduled",
      "meeting.agenda_built",
      "meeting.agenda_changed",
      "meeting.started",
      "meeting.updated",
      "meeting.outcome_recorded",
      "meeting.rated",
      "meeting.ended",
      "meeting.cancelled",
      "meeting.preread_requested",
      "vision_page.updated",
    ],
  },
  {
    key: "calendar",
    label: "Calendar",
    types: [
      "calendar.updated",
      "calendar.published",
      "calendar.unpublished",
      "calendar.shared",
      "calendar.share_revoked",
      "calendar.follow_changed",
      "calendar_event.created",
      "calendar_event.updated",
      "calendar_event.cancelled",
      "calendar_event.responded",
      "calendar_feed.created",
      "calendar_feed.revoked",
    ],
  },
  {
    // M8-ASSESS
    key: "assessments",
    label: "Assessments",
    types: [
      "assessment.round_created",
      "assessment.round_updated",
      "assessment.round_deleted",
      "assessment.round_opened",
      "assessment.round_closed",
      "assessment.report_generated",
      "assessment.participant_invited",
      "assessment.participant_removed",
      "assessment.participant_reminded",
      "assessment.link_issued",
      "assessment.response_saved",
      "assessment.response_submitted",
      "assessment.proposal_decided",
      "assessment.proposal_applied",
    ],
  },
  {
    key: "agents",
    label: "Agents and runs",
    types: [
      // DOCS-KB
      "agent.knowledge_changed",
      "agent_profile.created",
      "agent_profile.updated",
      "agent_profile.retired",
      "wakeup.requested",
      "wakeup.scheduled",
      "wakeup.coalesced",
      "wakeup.dropped",
      "run.queued",
      "run.started",
      "run.step_recorded",
      "run.waiting",
      "run.completed",
      "run.failed",
      "run.cancelled",
      "run.delegated",
      "run.worker_refused",
      "run.parked",
      "run.unparked",
      "sandbox.started",
      "sandbox.stopped",
      "skill_pack.published",
      "skill_pack.deprecated",
      "skill_pack.roles_changed",
      "skill_pack.archived",
      "skill_pack.restored",
      "agent_policy.changed",
      "agent_policy.dispatch_refused",
      "agent_request.created",
      "agent_request.resolved",
      "agent_request.stage_changed",
      "classifier.decided",
      "decision_rubric.saved",
      "agent.guardrail_tripped",
      "agent.reach_changed",
      "agent.reach_requested",
      "agent.reach_request_decided",
      "structure.changed",
      "structure.rerouted",
      "seat.serves_changed",
      "chief_of_staff.mode_changed",
      "chief_of_staff.mode_suggested",
      "brief_settings.updated",
      "brief_rule.updated",
      "recall.personal_grant_changed",
      "approval.requested",
      "approval.decided",
      "approval.expired",
      "approval.executed",
      "delegation_request.sent",
      "delegation_request.accepted",
      "delegation_request.declined",
      "delegation_request.expired",
      "delegation_request.completed",
      // Personal crews (M4-01).
      "personal_crew.created",
      "personal_crew.updated",
      "personal_crew.paused",
      "personal_crew.resumed",
      "personal_crew.deactivated",
      "personal_crew.onboarding_answered",
      "crew_agent.added",
      "crew_agent.removed",
      "role_skill_policy.changed",
      "autonomy_policy.changed",
      "sharing_policy.changed",
      "crew_context.updated",
      "crew_cap.changed",
      // Crew features (M4-03).
      "personal_crew.features_changed",
      "crew_brief.posted",
      "crew_proposal.created",
      "crew_proposal.decided",
      "crew_memory_grant.changed",
      // Hire as DE (M7-05).
      "crew.hired_as_de",
      // WORKFORCE: agents (AG) under a DE or a person.
      "workforce_agent.registered",
      "workforce_agent.updated",
      "workforce_agent.use_changed",
      "agent.autonomy_changed",
      "agent_policy.decided",
      "run.failures_continued",
      "run.loop_warned",
      "run.loop_stopped",
      "loop_breaker.default_changed",
      "loop_incident.resolved",
      "recovery.opened",
      "recovery.resolved",
      "incident.opened",
      "incident.resolved",
      "incident.reverted",
      "voice_session.opened",
      "live.interacted",
      "canvas.op_written",
    ],
  },
  {
    key: "de_config",
    label: "DE configuration",
    types: [
      "agent_template.created",
      "agent_template_version.drafted",
      "agent_template_version.draft_updated",
      "agent_template_version.validated",
      "agent_template_version.validation_failed",
      "agent_template_version.published",
      "agent_template_version.deprecated",
      "template_edit.proposed",
      "template_edit.accepted",
      "template_edit.rejected",
      "deploy.staged",
      "deploy.approved",
      "deploy.rejected",
      "deploy.handed_off",
      "deploy.confirmed",
      "deploy.failed",
      "verification.overridden",
      "digital_employee.deployed",
      "digital_employee.deploy_failed",
      "digital_employee.panel_drift",
      "de_template_override.changed",
      "template_overlay.changed",
      "digital_employee.role_applied",
      "de_access_default.changed",
      "digital_employee.probation_ended",
      "model_credential.added",
      "model_credential.revoked",
      "model_credential.updated",
      "subscription_attestation.recorded",
      "subscription_attestation.revoked",
      "tool_connection.added",
      "tool_connection.checked",
      "tool_connection.revoked",
      // Connections (M4-04).
      "tool_connection.refreshed",
      "tool_connection.reconnected",
      "crew_connection_grant.changed",
      // INC2-INTAKE: the integration work queue's dead letters
      "connection.job_dead",
      "integration_job.resolved",
      "launch_token.minted",
      "launch_token.used",
      "run.verdict_recorded",
      "run.decision_recorded",
      "run.premise_escalated",
      "run.resumed",
      "drift_audit.scheduled",
      "drift_audit.started",
      "drift_audit.passed",
      "drift_audit.failed",
      "drift_audit.errored",
      // STEP1-OUTBOX: who may use a connection, and the external-action outbox.
      "connection.grant_changed",
      "outbox.queued",
      "outbox.sent",
      "outbox.failed",
      "outbox.retried",
      "outbox.dead",
      "outbox.cancelled",
      "agent_spec.versioned",
      "skill_pack.additions_changed",
      "playbook.links_changed",
      "procedure.run_recorded",
      "digital_employee.provision_requested",
      "digital_employee.connect_requested",
      "digital_employee.credentials_claimed",
      "digital_employee.bridge_registered",
      "digital_employee.bridge_removed",
    ],
  },
  {
    key: "screens",
    label: "Screens",
    types: [
      "screen_stack.registered",
      "screen_stack.health_changed",
      "screen_lease.queued",
      "screen_lease.started",
      "screen_lease.bumped",
      "screen_lease.needs_you",
      "screen_lease.taken_over",
      "screen_lease.handed_back",
      "screen_lease.ended",
      "screen_ask.resolved",
      "screen_ask.extended",
      "browser_account.created",
      "browser_account.status_changed",
      "browser_account.granted",
      "browser_account.grant_revoked",
      "browser_account.updated",
      "browser_account.revoked",
    ],
  },
  {
    key: "finance",
    label: "Finance",
    types: [
      "helper_offer.changed",
      "budget.changed",
      "budget.reserved",
      "budget.settled",
      "budget.released",
      "budget.reservation_refused",
      "budget.threshold_crossed",
      "budget.exceeded",
      "budget.overrun_approved",
      "budget.pace_alert",
      "budget.reserve_reached",
      "budget.period_reset",
      "price_list.changed",
      "fx_rate.set",
    ],
  },
  {
    key: "work",
    label: "Work",
    types: [
      "space.created",
      "space.updated",
      "space.archived",
      "space.unarchived",
      "space.moved",
      "attachment.uploaded",
      "attachment.attached",
      "attachment.removed",
      "attachment.saved",
      "follow.changed",
      "inbox_item.created",
      "inbox_item.read",
      "inbox_item.done",
      "inbox_item.read_all",
      "inbox_item.snoozed",
      "inbox_item.nudged",
      "inbox_item.escalation_due",
      "notification_prefs.changed",
      "board.created",
      "board.gates_changed",
      "item_template.created",
      "item_template.updated",
      "item_template.archived",
      "board.updated",
      "board.archived",
      "board.unarchived",
      "board.moved",
      "view.created",
      "view.updated",
      "view.deleted",
      "field_def.created",
      "field_def.updated",
      "field_def.deleted",
      "task.created",
      "task.gate_held",
      "task.gate_pulled",
      "task.gate_passed",
      "reply_style.changed",
      "task.updated",
      "task.status_changed",
      "task.assigned",
      "task.unassigned",
      "task.field_changed",
      "task.due_changed",
      "task.priority_changed",
      "task.checklist_changed",
      "task.dependency_changed",
      "task.moved",
      "task.undone",
      "task.deleted",
      // CARD-ARCHIVE
      "task.archived",
      "task.unarchived",
      // WOS-BUNDLE
      "task.tagged",
      "task.untagged",
      "task.linked",
      "task.unlinked",
      "task.duplicated",
      "task.timer_started",
      "task.timer_stopped",
      "board.duplicated",
      "space.duplicated",
      "tag.created",
      "tag.updated",
      "tag.merged",
      "tag.deleted",
      "favorite.added",
      "favorite.removed",
      "favorite.reordered",
      "recent.viewed",
      "my_task_section.created",
      "my_task_section.updated",
      "my_task_section.deleted",
      "my_task.placed",
      // TASK-FILES
      "task.file_changed",
      "task.file_folder_changed",
      "task.trash_emptied",
      // M7-02
      "page.created",
      "page.shares_changed",
      "page.edited",
      "page.moved",
      "page.archived",
      // DOCS-KB
      "page.unarchived",
      "page.deleted",
      "page.undeleted",
      // Work OS parity (P1–P7)
      "pref.changed",
      "field.required_changed",
      "board.task_layout_changed",
      "space.default_task_layout_changed",
      "task.due_soon",
      "task.overdue",
      "task.handed_off",
      // COS-MODES
      "task.agent_directed",
      "task.taken_back",
      // COS-SLICE1
      "task.handoff_parked",
      "cos_digest.posted",
      "suggestion.created",
      "suggestion.accepted",
      "suggestion.dismissed",
      "task.observation_recorded",
      "automation.created",
      "automation.updated",
      "automation.paused",
      "automation.deleted",
      "automation.fired",
      "automation.refused",
      "recurring.paused",
      "recurring.resumed",
      "recurring.skipped",
      "recurring.taken_over",
      "task.move_refused",
      "task.results_due",
      "commitment.created",
      "commitment.changed",
      "page.members_changed",
      "page.upload_queued",
      "page.upload_changed",
      "ooo.window_set",
      "ooo.window_ended",
      "ooo.summary_seen",
    ],
  },
  {
    key: "chat",
    label: "Chat",
    types: [
      "channel.created",
      "channel.shares_changed",
      "channel.updated",
      "channel.archived",
      "channel.restored",
      "channel.deleted",
      "channel.member_changed",
      "channel.read",
      // THREAD-READS: per-thread read positions and follows.
      "thread.read",
      "thread.follow_changed",
      "thread.agent_left",
      "channel.prefs_changed",
      // CHAT-PRESENCE: closing and reopening DMs; a stuck DE's failure seen.
      "channel.dm_closed",
      "channel.dm_reopened",
      "run.failure_acknowledged",
      "channel.agent_rules_changed",
      "agent_room.taken_over",
      "agent_room.released",
      // M4-05: rooms with turns, and agents asking each other for a task's status.
      "agent_room.created",
      "agent_room.updated",
      "agent_room.participant_changed",
      "agent_room.turn_changed",
      "agent_room.removed",
      "status_ask.created",
      "status_ask.answered",
      "status_ask.escalated",
      "status_ask.declined",
      "topic.created",
      "topic.renamed",
      "topic.resolved",
      "topic.pinned",
      "topic.archived",
      "topic.deleted",
      "topic.moved",
      "topic.split",
      "topic.member_changed",
      "topic.privacy_changed",
      "message.created",
      "message.edited",
      "message.deleted",
      "message.pin_changed",
      "message.bookmark_changed",
      "reaction.added",
      "reaction.removed",
      "member_status.changed",
      // CHAT-SWITCH-B: asks, channel webhooks, scheduled messages and link previews
      "chat_ask.requested",
      "chat_ask.answered",
      "chat_webhook.created",
      "chat_webhook.rotated",
      "chat_webhook.revoked",
      "scheduled_message.created",
      "scheduled_message.updated",
      "scheduled_message.cancelled",
      "scheduled_message.sent",
      "message.preview_hidden",
    ],
  },
  {
    key: "reports",
    label: "Reports",
    types: [
      "scorecard.published",
      "friday_report.generated",
      "friday_report.updated",
      "friday_report.sent",
      "friday_report.send_failed",
      "friday_report.delivery_reconciled",
    ],
  },
  {
    key: "ledger",
    label: "Costs and time",
    types: [
      "cost.posted",
      "cost.meter_settled",
      "de_subscription.created",
      "de_subscription.changed",
      "time_entry.created",
      "time_entry.updated",
      "time_entry.deleted",
      "time_entry.approval_changed",
    ],
  },
  {
    key: "finance",
    label: "Finance",
    types: [
      "category.changed",
      "client.changed",
      "revenue.posted",
      "compensation.changed",
      "recurring_expense.changed",
      "forecast.snapshotted",
      "scenario.created",
      "scenario.evaluated",
      "scenario.updated",
      "scenario.deleted",
      "headcount_plan.changed",
      "finance.exported",
    ],
  },
  {
    key: "wallet",
    label: "Wallet",
    types: [
      "wallet.checkout_started",
      "wallet.topup_invoiced",
      "wallet.credited",
      "wallet.refunded",
      "wallet.adjusted",
      "wallet.low",
      "wallet.empty",
      "wallet.restored",
      "wallet.settings_changed",
      "tariff.changed",
      "tariff_catalog.changed",
      "topup_bonus_tier.changed",
    ],
  },
  {
    key: "ingest",
    label: "Ingest",
    types: ["activity.recorded", "outcome.recorded"],
  },
  {
    key: "operations",
    label: "Operations",
    types: [
      "import.committed",
      "import.reversed",
      "consumer.record_retried",
      "consumer.record_skipped",
      "import_run.created",
      "import_run.mapping_updated",
      "import_run.queued",
      "import_run.started",
      "import_run.progressed",
      "import_run.succeeded",
      "import_run.failed",
      "import_run.cancel_requested",
      "import_run.cancelled",
      "service_endpoint.changed",
      "effort_defaults.changed",
      "control_token.created",
      "control_token.paired",
      "control_token.revoked",
      "tool_connection.mail_sync_changed",
      "insight.asked",
      "insight_widget.changed",
    ],
  },
  {
    key: "notifications",
    label: "Notifications",
    types: ["push_subscription.added", "push_subscription.removed"],
  },
  {
    key: "memory",
    label: "Memory and Recall",
    types: [
      "recall.notice_received",
      "recall.tenant_provisioned",
      "recall.reconciled",
      "recall.agent_key_issued",
      "recall.agent_revoked",
      "recall.memory_copied",
      "recall.kb_configured",
      "promotion.proposed",
      "promotion.accepted",
      "promotion.rejected",
      "decision.recorded",
      "reminder.created",
      "reminder.fired",
      "reminder.dismissed",
      "reminder.updated",
      "decision.stance_recorded",
      "decision.outcome_answered",
      "memory.charter_set",
      "memory.governance_changed",
      "memory.onboarding_changed",
      "memory.profile_decided",
      "memory.acting_ended",
      "memory.catch_up_closed",
      "memory.succession_answered",
      "memory.succession_settled",
      "memory_admin.acted",
      "memory_admin.job_changed",
      "memory_contradiction.settled",
      "memory_lane.decided",
      "memory_lane.bulk_approved",
      "memory_lane.see_all_changed",
      "memory_proposals.settled",
      "memory_quality.archive_suggested",
      "memory_quality.archive_applied",
      "memory_rule.changed",
    ],
  },
  {
    key: "billing",
    label: "Billing",
    types: [
      "billing.account_changed",
      "billing.period_closed",
      "billing.price_changed",
      "billing_charge.created",
      "billing_operation.failed",
      "billing_operation.retried",
      "billing_operation.dismissed",
      "subscription.created",
      "subscription.updated",
      "subscription.quantity_changed",
      "subscription.reminded",
      "subscription.past_due",
      "subscription.suspended",
      "subscription.restored",
      "subscription.cancelled",
      "de_maintenance.changed",
      "discount.granted",
      "discount.ended",
      "invoice.created",
      "invoice.finalized",
      "invoice.sent",
      "invoice.reminded",
      "invoice.paid",
      "invoice.marked_paid",
      "invoice.payment_failed",
      "invoice.overdue",
      "invoice.marked_uncollectible",
      "invoice.voided",
      "quote.changed",
      "project.changed",
      "project.milestone_invoiced",
      "workforce_agent.cap_refused",
    ],
  },
];

export const ALL_EVENT_TYPES: readonly string[] = EVENT_GROUPS.flatMap((g) => g.types);
